import { useMutation } from "@apollo/client"
import { useSSRDisabled } from "@skyltmax/ds9/hooks"
import cn from "classnames"
import React, { useCallback } from "react"
import { useTranslation } from "react-i18next"
import { graphql } from "@/api"
import LoadingAnimation from "@/common/components/loading"
import useMedia from "@/common/hooks/use_media"
import tailwindTheme from "@/common/styles/theme.module.css"
import { toggleScroll, basketAddToGA4 } from "@/common/util"
import { setBasketStateDoc } from "@/menu/components/basket_modal"
import { CartFill } from "@/skylticons"

const addDoc = graphql(`
  mutation BundleBasketAdd($bundleId: ID!, $recommended: String, $signCategoryId: Int, $examplerId: Int) {
    bundleBasketAdd(
      bundleId: $bundleId
      recommended: $recommended
      signCategoryId: $signCategoryId
      examplerId: $examplerId
    ) {
      success

      item {
        id
        priceSek
        totalCount
      }

      order {
        ...BasketModal_Order
      }
    }
  }
`)

export interface AddButtonProps {
  loading?: boolean
  onClick: (e: React.MouseEvent) => void
  secondaryNoIcon?: boolean
  dataGtm?: string
  className?: string
  copyBtn?: boolean
}

export const AddButton: React.FC<AddButtonProps> = ({
  loading,
  onClick,
  secondaryNoIcon,
  dataGtm,
  className,
  copyBtn,
}) => {
  const { t } = useTranslation()

  return (
    <button
      id="add_to_cart"
      className={cn("example-to-cart button mb-0 ml-0 xl:flex-1", className, {
        "full-width": !className,
        "!button-secondary": !className && secondaryNoIcon,
        "button-primary": !className && !secondaryNoIcon,
        "button-medium": !copyBtn && !className,
        "button-secondary button-small": copyBtn,
      })}
      type="button"
      onClick={onClick}
      disabled={loading}
      data-gtm={`${dataGtm || "example-sign-add-to-cart"}`}
    >
      {loading ? (
        <LoadingAnimation />
      ) : (
        <span className="example-to-cart flex items-center justify-center">
          {!secondaryNoIcon && !copyBtn && <CartFill className="example-to-cart" />}
          {copyBtn ? t("order.copy_to_cart") : t("signomatic.add_to_basket")}
        </span>
      )}
    </button>
  )
}

export interface AddBundleToCartProps {
  bundleId: string
  recommended?: string
  secondaryNoIcon?: boolean
  dataGtm?: string
  className?: string
  signCategoryId?: number
  examplerId?: number
  copyBtn?: boolean
}

const AddBundleToCart: React.FC<AddBundleToCartProps> = ({
  bundleId,
  recommended,
  dataGtm,
  secondaryNoIcon,
  className,
  signCategoryId,
  examplerId,
  copyBtn,
}) => {
  const [setBasketState] = useMutation(setBasketStateDoc)
  const desktop = useMedia(`(min-width: ${tailwindTheme.screensXl})`)
  const ssrDisabled = useSSRDisabled()

  const openBasket = useCallback(() => {
    setBasketState({ variables: { open: true, addedNotice: true } })

    if (!desktop) {
      toggleScroll(true)
    }
  }, [setBasketState, desktop])

  const [addMutation, addMutationResult] = useMutation(addDoc, {
    update: (store, { data: bundleBasketAddData }) => {
      if (bundleBasketAddData?.bundleBasketAdd?.success) {
        const orderItem = bundleBasketAddData.bundleBasketAdd.item
        basketAddToGA4(orderItem.id, orderItem.priceSek, orderItem.totalCount)
        openBasket()
      }
    },
  })

  const addBasket = () => {
    addMutation({
      variables: {
        bundleId,
        recommended,
        signCategoryId,
        examplerId,
      },
    })
  }

  return (
    <AddButton
      loading={ssrDisabled || addMutationResult.loading}
      onClick={addBasket}
      secondaryNoIcon={secondaryNoIcon}
      dataGtm={dataGtm}
      className={className}
      copyBtn={copyBtn}
    />
  )
}

export default AddBundleToCart
